import React, { useState, useEffect } from 'react';
import { mobileObjectDetailService } from '../services/mobileObjectDetailService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';
import ImageTagInputModal from './ImageTagInputModal';

// CSS-in-JS スタイル定義
const styles = {
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 3000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalContent: {
    background: 'white',
    padding: '20px',
    borderRadius: '10px',
    maxWidth: '90%',
    maxHeight: '70%',
    overflow: 'auto',
    width: '90%'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '1px solid #ddd'
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333'
  },
  closeButton: {
    background: '#666',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  imageList: {
    marginBottom: '20px'
  },
  imageItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    marginBottom: '10px',
    backgroundColor: '#f9f9f9'
  },
  imageNumber: {
    width: '30px',
    textAlign: 'center',
    fontWeight: 'bold',
    marginRight: '10px'
  },
  imageThumbnail: {
    width: '60px',
    height: '60px',
    objectFit: 'contain',
    borderRadius: '5px',
    marginRight: '10px',
    backgroundColor: '#2B2222'
  },
  imageInfo: {
    flex: 1,
    marginRight: '10px'
  },
  imageTags: {
    fontSize: '12px',
    color: '#666'
  },
  tagButton: {
    background: '#f0f0f0',
    color: '#333',
    border: '1px solid #ccc',
    borderRadius: '5px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '12px',
    width: '30%'
  },
  registerButton: {
    width: '100%',
    background: '#F93535',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    padding: '12px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  noImages: {
    textAlign: 'center',
    color: '#666',
    padding: '20px'
  }
};

const ImageTagListModal = ({ 
  isOpen, 
  onClose, 
  objectId, 
  images = [], 
  imageData = [],
  onTagsUpdated 
}) => {
  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false));
  
  // UI状態
  const [isLoading, setIsLoading] = useState(false); // ローディング状態
  const [imageTags, setImageTags] = useState({}); // 画像タグ一覧（インデックス別）
  const [showTagInputModal, setShowTagInputModal] = useState(false); // タグ入力モーダル表示フラグ
  const [selectedImageIndex, setSelectedImageIndex] = useState(null); // 選択中の画像インデックス

  // モーダル初期化処理
  useEffect(() => {
    if (isOpen && objectId && images.length > 0) {
      loadImageTags();
    }
  }, [isOpen, objectId, images.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // 画像タグ読み込み処理
  const loadImageTags = async () => {
    try {
      setIsLoading(true);
      const tags = {};
      
      let errorCount = 0;

      // 各画像のタグを取得
      for (let i = 0; i < images.length; i++) {

        try {
          const imageDataItem = imageData[i];
          if (imageDataItem !== null && imageDataItem.objectImageId !== null 
            && imageDataItem.objectImageId !== undefined && imageDataItem.objectImageId !== '' ) {
            const tagData = await mobileObjectDetailService.getObjectImageTag(
              objectId, 
              imageDataItem.objectImageId
            );
            
            // ビジネスロジックエラーのチェック
            if (tagData.returnCode !== '200') {
              tags[i] = [];
              errorCount++;
              continue;
            }
            
            // レスポンスからimageTagListを取得し、imageTagId順にソート
            if (tagData.imageTagList && tagData.imageTagList.length > 0) {
              const sortedTagList = tagData.imageTagList
                .sort((a, b) => a.imageTagId - b.imageTagId);
              
              tags[i] = sortedTagList.map(tag => tag.imageTag);
            } else {
              tags[i] = [];
            }
          } else {
            tags[i] = [];
          }
        } catch (error) {
          tags[i] = [];
          errorCount++;
        }
      }
      
      setImageTags(tags);

      if (errorCount > 0) {
        throw new Error('画像タグの取得に失敗しました。');
      }
    } catch (error) {
      handleApiError(new Error('画像タグの取得に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  };

  // タグ設定モーダル表示処理
  const handleTagSettings = (imageIndex) => {
    setSelectedImageIndex(imageIndex);
    setShowTagInputModal(true);
  };

  // タグ入力モーダル閉じる処理
  const handleCloseTagInputModal = () => {
    setShowTagInputModal(false);
    setSelectedImageIndex(null);
  };

  // タグ更新処理
  const handleTagUpdate = (imageIndex, newTags) => {
    setImageTags(prev => ({
      ...prev,
      [imageIndex]: newTags
    }));
  };

  // タグ登録処理
  const handleRegisterTags = async () => {
    try {
      setIsLoading(true);

      let successCount = 0;
      let errorCount = 0;
      
      // 全ての画像のタグを処理
      for (let i = 0; i < images.length; i++) {
        const imageDataItem = imageData[i];
        if (imageDataItem !== null && imageDataItem.objectImageId !== null 
          && imageDataItem.objectImageId !== undefined && imageDataItem.objectImageId !== '' ) {
          const currentTags = imageTags[i] || [];
          
          // 画像タグ全削除→新規登録の形式でデータを準備
          const tagData = {
            objectId: objectId,
            objectImageId: imageDataItem.objectImageId,
            imageTagList: currentTags.map((tag, index) => ({
              imageTagId: index + 1, // 新規登録なので連番でIDを割り当て
              imageTag: tag,
              deleteFlg: 0
            }))
          };
          
          // addObjectImageTagメソッドを呼び出し（全削除→新規登録）
          const result = await mobileObjectDetailService.addObjectImageTag(tagData);
          
          // ビジネスロジックエラーのチェック
          if (result.returnCode !== '200') {
            errorCount++;
            continue;
          }
          successCount++;
        }
      }

      // タグ登録結果を処理
      if (errorCount > 0) {
        MessageDialog.show('エラー', `画像タグの登録に失敗しました。`, null, 'error');
      }
      
      // 親コンポーネントに登録完了を通知
      if (onTagsUpdated && successCount > 0) {
        onTagsUpdated(null, null, true); 
      }
      
      // タグ登録が成功した場合のみモーダルを閉じる
      if (successCount > 0) {
        onClose();
      }

    } catch (error) {
      // エラーが発生した場合は、エラーダイアログを表示してモーダルを閉じない
      handleApiError(new Error('画像タグの登録に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.header}>
          <div style={styles.title}>画像タグ設定</div>
          <button style={styles.closeButton} onClick={onClose}>
            戻る
          </button>
        </div>

        <div style={styles.imageList}>
          {images.length === 0 ? (
            <div style={styles.noImages}>画像がありません</div>
          ) : (
            images.map((image, index) => (
              <div key={index} style={styles.imageItem}>
                <div style={styles.imageNumber}>{index + 1}</div>
                <img 
                  src={image} 
                  alt={`画像 ${index + 1}`}
                  style={styles.imageThumbnail}
                  onError={(e) => {
                    e.target.src = '/core/img/errorimage.png';
                  }}
                />
                <div style={styles.imageInfo}>
                  <div style={styles.imageTags}>
                    タグ: {imageTags[index]?.join(', ') || 'なし'}
                  </div>
                </div>
                <button 
                  style={styles.tagButton}
                  onClick={() => handleTagSettings(index)}
                >
                  タグ設定
                </button>
              </div>
            ))
          )}
        </div>

        {images.length > 0 && (
          <button 
            style={styles.registerButton}
            onClick={handleRegisterTags}
            disabled={isLoading}
          >
            {isLoading ? '登録中...' : 'タグ登録'}
          </button>
         )}
       </div>

       {/* タグ入力モーダル */}
       <ImageTagInputModal
         isOpen={showTagInputModal}
         onClose={handleCloseTagInputModal}
         imageIndex={selectedImageIndex}
         currentTags={selectedImageIndex !== null ? imageTags[selectedImageIndex] || [] : []}
         onTagsUpdate={handleTagUpdate}
       />

       {/* Error Dialog */}
       <MessageDialog
         isOpen={showErrorDialog}
         onClose={closeErrorDialog}
         title="メッセージ"
         message={errorMessage}
         type="error"
       />
     </div>
   );
 };
 
 export default ImageTagListModal;
